// @ts-check

import { TIME_UP_LOCKED_TYPES } from 'Components/Classroom/Content/constant';
import { LEARNING_MODE, useClassroomContext } from 'Context/ClassroomContext';
import { getSubsectionInMode } from 'Util/Classroom/learningModeHelper';

import useIsClassroomTimeUp from './useIsClassroomTimeUp';

const updateFlutterHeader = (courseCode, sectionTitle, subsectionTitle, subSectionType) => {
  const args = { courseCode, sectionTitle, subsectionTitle, subSectionType };
  window.flutter_inappwebview?.callHandler('handleSectionTitle', args);
};

const useClassroomNavigation = () => {
  const {
    updateCurrentSubsection,
    currentSubsectionId,
    courseData,
    hasClassroomSession,
    currentLearningMode,
  } = useClassroomContext();
  const isTimeUp = useIsClassroomTimeUp();

  if (!courseData || !hasClassroomSession) return {};

  const { sections: originalSections = [], subsections: originalSubsections = [] } = courseData;

  const currentSubsectionIndex = originalSubsections.findIndex(
    ({ id }) => id === currentSubsectionId
  );

  const subsectionsInMode = getSubsectionInMode(
    originalSubsections,
    currentLearningMode ?? LEARNING_MODE.DEFAULT
  );

  // Once learning time runs out, next/previous skip the content that would only show the time-up modal
  const canGoTo = ({ id, contentType }) => {
    const isInMode = subsectionsInMode.findIndex((subsection) => subsection.id === id) >= 0;
    const isTimeLocked = isTimeUp && TIME_UP_LOCKED_TYPES.includes(contentType);
    return isInMode && !isTimeLocked;
  };

  const nextSubsection = originalSubsections
    .slice(currentSubsectionIndex + 1, originalSubsections.length)
    .find(canGoTo);

  const previousSubsection =
    currentSubsectionIndex >= 0
      ? originalSubsections.slice(0, currentSubsectionIndex).reverse().find(canGoTo)
      : null;

  const currentSubsection =
    currentSubsectionIndex >= 0 && currentSubsectionIndex < originalSubsections.length
      ? originalSubsections[currentSubsectionIndex]
      : null;

  const currentSection = originalSections.find(
    (section) => section.id === currentSubsection?.sectionId
  );

  return {
    isFirstSubsectionInCourse: !previousSubsection,
    isLastSubsectionInCourse: !nextSubsection,
    previousSubsection,
    currentSection,
    currentSubsection,
    nextSubsection,
    goToNextSubsection: () => {
      if (!nextSubsection) return;
      updateCurrentSubsection(nextSubsection.id, nextSubsection.referId);
    },
    goToPreviousSubsection: () => {
      if (!previousSubsection) return;
      updateCurrentSubsection(previousSubsection.id, previousSubsection.referId);
    },
    goToSubsection: updateCurrentSubsection,
    updateFlutterHeader: (courseCode) =>
      updateFlutterHeader(
        courseCode,
        currentSection?.title,
        currentSubsection?.title,
        currentSubsection?.contentType
      ),
  };
};

export default useClassroomNavigation;

/**
 * @typedef {import('Context/ClassroomContext/context').SectionGroup} SectionGroup
 * @typedef {import('Context/ClassroomContext/context').Section} Section
 * @typedef {import('Context/ClassroomContext/context').FlattedSubsection} Subsection
 * @typedef {import('Context/ClassroomContext/context').LearningMode} LearningMode
 */
